// CANVAS-BLOCKS: a person's clicks on the Workspace's blocks, told to the agent (`liveCanvas.
// interact` appends an `interact` op to the Live session, which the agent reads beside what was
// said). Fire and forget: a click never waits on it, and a refusal or a dropped request is
// swallowed (the click itself already happened). The in-memory API has no Live session, so only
// the HTTP client reports.
import { useCallback } from "react";
import type { Api } from "../../api/client";
import type { CanvasOp } from "./canvas-state";
import type { Placed } from "./desk-layout";

export type CanvasInteraction = Extract<CanvasOp, { op: "interact" }>["action"];

export type CanvasInteractOp = {
  action: CanvasInteraction;
  block_id?: string | undefined;
  item_id?: string | undefined;
  /** A short note (the card's title, the column moved into); cut to 200 characters. */
  detail?: string | undefined;
};

export type CanvasInteract = (op: CanvasInteractOp) => void;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The interaction as the route takes it: an empty detail and a non-uuid item are left out. */
export function interactBody(sessionId: string, op: CanvasInteractOp) {
  const detail = op.detail?.trim().slice(0, 200);
  return {
    session_id: sessionId,
    op: {
      op: "interact" as const,
      action: op.action,
      ...(op.block_id ? { block_id: op.block_id } : {}),
      ...(op.item_id && UUID.test(op.item_id) ? { item_id: op.item_id } : {}),
      ...(detail ? { detail } : {}),
    },
  };
}

/**
 * LIVE-DESKTOP: the page's own desk ops for its session (`liveCanvas.interact` takes them too):
 * `place` (geometry), `clear` of named windows (closed by the person). Fire and forget, as above.
 */
export type DeskWriteOp = { op: "place"; windows: Placed[] } | { op: "clear"; block_ids: string[] };
export type DeskWrite = (op: DeskWriteOp) => void;

export function useDeskWrite(
  api: Api,
  companyId: string,
  sessionId: string | null | undefined,
): DeskWrite {
  return useCallback(
    (op: DeskWriteOp) => {
      if (api.mode !== "http" || !sessionId) return;
      try {
        void api.client.liveCanvas
          .interact({ params: { co: companyId }, body: { session_id: sessionId, op } })
          .catch(() => undefined);
      } catch {
        // A client without the route says nothing.
      }
    },
    [api, companyId, sessionId],
  );
}

export function useCanvasInteract(
  api: Api,
  companyId: string,
  sessionId: string | null | undefined,
): CanvasInteract {
  return useCallback(
    (op: CanvasInteractOp) => {
      if (api.mode !== "http" || !sessionId) return;
      try {
        void api.client.liveCanvas
          .interact({ params: { co: companyId }, body: interactBody(sessionId, op) })
          .catch(() => undefined);
      } catch {
        // A client without the route (an older SDK) says nothing.
      }
    },
    [api, companyId, sessionId],
  );
}
